<template lang="pug">
  modal.modal(:title='$t("kreaCreateInTeamModal.title")' @closeModal='hideElement({ element: "kreaCreateInTeamModal" })')
    .column.middle.m-top-1
      p.text-normal.dark-gray.m-bottom-2 {{ $t("kreaCreateInTeamModal.description") }}
      share-modal-filters.m-bottom-1(@search='filterUsers')
      share-modal-list(
        withoutGroups
        @selectAll='toggleSelectAll'
        :users='usersList'
        :selectedUsers='selectedUsers'
        :loading='loadingUsers'
      )
        .user.row.middle(v-for='item in usersList' :key='item.id')
          checkbox.m-right-1(
            v-if='!item.associatedPreviously'
            name='user'
            :checked='isUserSelected(item.id)'
            :updateCheck='() => toggleSelected(item)'
            transparent
          )
          img.avatar(:src='item.avatar || defaultProfileImage')
          .column.m-left-1.item-1
            .row.middle.ellipsis.username
              h2.text-normal.m-right-05 {{ item.name }}
              h2.text-normal.ellipsis(
                v-if='(item.position.positionId || item.schoolName)'
                :title='getPositionWithSchoolName(item.position)'
              ) {{ getPositionWithSchoolName(item.position) }}
            h4.text-small.dark-gray {{ item.email }}
          .column.m-left-auto.select.dark-gray(v-if='item.permission === 3')
            | {{ $t("folders.shareModal.owner") }}
          .column.m-left-auto.select(v-if='item.permission !== 3 && (isUserSelected(item.id) || item.associatedPreviously)')
            k-select(
              name='permission'
              keyName='value'
              labelName='label'
              :preselectedOption='getSelectedPermission(item.id)'
              :options='permissionsOptions'
              @selectOption='(name, value) => editUserPermission(value, item.id)'
              transparent
            )
      .row.end.middle
        button.with-icon.primary-button.max-width-none(@click='submit')
          simple-svg.m-right-1.white-icon(:filepath='sendIcon' width='18px' height='18px')
          | {{ $t("kreaCreateInTeamModal.invite") }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ShareModalFilters from '@/components/ShareModalFilters'
import ShareModalList from '@/components/ShareModalList'
import Checkbox from '@/components/Inputs/Checkbox'
import KSelect from '@/components/KSelect'
import Modal from '../Modal'
import sendIcon from '@/assets/ic_send.svg'
import userPosition from '@/mixins/userPosition'
import { deepCopy } from '../../utils/object'
import defaultProfileImage from '@/assets/empty_profile.svg'
import { kreaCollaborativePermissions, kreaCollaborativePermissionsIcons } from '@/constants/krea'
import { i18n } from '../../main'

export default {
  mixins: [userPosition],
  components: {
    Modal,
    ShareModalFilters,
    ShareModalList,
    Checkbox,
    KSelect
  },
  data() {
    return {
      sendIcon,
      defaultProfileImage,
      loadingUsers: true,
      previouslyAddedUsers: [],
      selectedUsers: [],
      filters: {}
    }
  },
  async created() {
    this.filters = { schoolId: this.userInfo.schoolId || null }
    if (this.modalProps.contentId) {
      this.filters.contentId = this.modalProps.contentId
    }
    await this.search(this.filters)
    if (this.modalProps.selectedUsers) {
      for (const user of this.modalProps.selectedUsers) {
        this.toggleSelected(user)
      }
    }
    if (this.modalProps.contentId) {
      for (const user of this.usersList) {
        if (user.associatedPreviously) this.toggleSelected(user, true)
      }
    }
    this.loadingUsers = false
  },
  computed: {
    ...mapState({
      allAuthors: state => state.userInfo.searchedAuthors,
      contentAuthors: state => state.kreaEducationalContent.searchedAuthors,
      userInfo: state => state.userInfo.userInfo,
      modalProps: state => state.behavior.kreaCreateInTeamModal.props
    }),
    users() {
      return this.modalProps.contentId
        ? this.contentAuthors
        : this.allAuthors
    },
    permissionsOptions() {
      return Object.keys(kreaCollaborativePermissions)
        .filter(key => key !== 'owner' && key !== 'comment')
        .map(key => ({
          value: kreaCollaborativePermissions[key],
          label: `
            <div class='row'>
              <img class='m4-right-2' src='${kreaCollaborativePermissionsIcons[key]}'/>
              <p class='text-normal'>${i18n.t('kreaCreateInTeamModal.kreaPermissions.' + key)}</p>
            </div>
          `
        }))
    },
    usersList() {
      const list = [
        ...this.users.map(({ user, associatedPreviously, permission, id }) => ({
          name: user.fullName,
          avatar: user.avatar,
          position: user.position,
          email: user.email,
          associatedPreviously,
          permission,
          id
        }))
      ]
      return list
        .sort((a, b) => a.name.localeCompare(b.name))
        .sort((a, b) => (a.associatedPreviously === b.associatedPreviously) ? 0 : a.associatedPreviously ? -1 : 1)
        .sort((a, b) => {
          if (a.permission === 3) return -1
          if (b.permission === 3) return 1
          return 0
        })
    }
  },
  methods: {
    ...mapActions([
      'searchAuthors',
      'searchKreaContentAuthors',
      'associateAuthorsToKreaContent',
      'hideElement',
      'showElement'
    ]),
    closeModal() {
      this.hideElement({ element: 'kreaCreateInTeamModal' })
    },
    resetSelectedUsers() {
      this.selectedUsers = []
    },
    isUserSelected(id) {
      return Boolean(this.selectedUsers.find(user => user.id === id))
    },
    toggleSelected(item, addedPreviously) {
      if (this.isUserSelected(item.id)) {
        this.selectedUsers = this.selectedUsers.filter(user => user.id !== item.id)
      } else {
        if (addedPreviously) {
          this.previouslyAddedUsers = [...this.previouslyAddedUsers, item]
        } else {
          this.selectedUsers = [...this.selectedUsers, { ...item, permission: kreaCollaborativePermissions.edit }]
        }
      }
    },
    editUserPermission(permission, id) {
      this.selectedUsers = this.selectedUsers.map(u => u.id === id ? ({ ...u, permission }) : u)
    },
    getSelectedPermission(id) {
      const user = this.selectedUsers.find(u => u.id === id) || this.previouslyAddedUsers.find(u => u.id === id)
      return user && user.permission
    },
    toggleSelectAll() {
      if (this.selectedUsers.length === this.usersList.length) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = deepCopy(this.usersList.map(u => ({ ...u, permission: kreaCollaborativePermissions.edit })))
      }
    },
    async search(filters) {
      if (this.modalProps.contentId) await this.searchKreaContentAuthors(filters)
      else await this.searchAuthors(filters)
    },
    async filterUsers(filters) {
      this.resetSelectedUsers()
      this.loadingUsers = true
      this.filters = { ...this.filters, ...filters }
      await this.search(this.filters)
      this.loadingUsers = false
    },
    async submit() {
      if (this.modalProps.onSubmit) {
        this.modalProps.onSubmit(this.selectedUsers)
        return this.closeModal()
      } else {
        const { success } = await this.associateAuthorsToKreaContent({
          id: this.modalProps.contentId,
          url: window.location.origin + `/krea/content/${this.modalProps.contentId}`,
          authors: [
            ...this.previouslyAddedUsers.map(u => ({ authorId: u.id, permission: u.permission })),
            ...this.selectedUsers.map(u => ({ authorId: u.id, permission: u.permission }))
          ]
        })
        this.closeModal()
        if (success) {
          this.showElement({
            element: 'successModal',
            props: { title: i18n.t('krea.createContent.invitedSuccess') }
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.modal{
  --max-width: 680px;
}

.select{
  width: 200px;
}

.user {
  padding: 10px;
  height: 50px;
  .username{
    max-width: 450px;
  }
  .avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
  }
  .group-avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid $gray;
  }
}
</style>
